package de.oevbs.kunde.kundenportal2

import de.oevbs.kosmos.ui.icons.CustomIcons
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kunde.kundenportal2.Pages.all
import de.oevbs.kunde.kundenportal2.authentication.PortalAuthority
import de.oevbs.kunde.kundenportal2.authentication.PortalSession
import de.oevbs.kunde.kundenportal2.components.headerImageLayout
import de.oevbs.kunde.kundenportal2.navigation.KupoRouter
import de.oevbs.kunde.kundenportal2.navigation.Page
import de.oevbs.kunde.kundenportal2.navigation.components.subPagesOverview
import de.oevbs.kunde.kundenportal2.services.account.delete.accountDeletePage
import de.oevbs.kunde.kundenportal2.services.account.password.updatePasswordPage
import de.oevbs.kunde.kundenportal2.services.claims.subPageClaimList
import de.oevbs.kunde.kundenportal2.services.consent.consentPage
import de.oevbs.kunde.kundenportal2.services.contact.contactMessagePage
import de.oevbs.kunde.kundenportal2.services.contract.contractsStart
import de.oevbs.kunde.kundenportal2.services.delivery.delivery
import de.oevbs.kunde.kundenportal2.services.deregistercar.deregisterCarStart
import de.oevbs.kunde.kundenportal2.services.digiservicecard.digitalServiceCardStart
import de.oevbs.kunde.kundenportal2.services.inbox.inbox
import de.oevbs.kunde.kundenportal2.services.intinscard.intInsCardStart
import de.oevbs.kunde.kundenportal2.services.lifeValueNotification.lifeValueStart
import de.oevbs.kunde.kundenportal2.services.mydata.bankAccountsPage
import de.oevbs.kunde.kundenportal2.services.mydata.contactInformationStart
import de.oevbs.kunde.kundenportal2.services.registration.*
import de.oevbs.kunde.kundenportal2.services.tax.taxStart
import de.oevbs.kunde.kundenportal2.util.hyphen
import dev.fritz2.core.RenderContext
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

/**
 * Route, die zum Ansteuern der Root-Page (Startseite) benutzt wird.
 *
 * Diese muss von außerhalb als Konstante verfügbar sein, um bspw. den Router initialisieren zu können, ohne dieses
 * [Pages]-Objekt zu referenzieren.
 * Dies ist notwendig, da das Pages-Objekt eine Abhängigkeit zum Router hat und somit **nach** diesem initialisiert
 * werden muss.
 */
const val ROOT_PAGE_ROUTE = "top"

/**
 * Zentrales Objekt, in dem die in der Anwendung verfügbaren Seiten definiert werden.
 *
 * Das Objekt beinhaltet neben den einzelnen Seiten ebenfalls einen Index aller verfügbarer Seiten in Form der
 * Property [all].
 *
 * Neue Seiten müssen in diesem Kontext mittels [Pages.register] registriert werden, damit sie in diesem Index
 * berücksichtigt werden können.
 *
 * Das Pages-Objekt wird verwendet, um den Inhalt der Navigationsleiste zu generieren. Darüber hinaus kann es benutzt
 * werden, um auch außerhalb der Navigation programmatisch zu einer Seite zu wechseln:
 *
 * ```
 * button {
 *     +"Jetzt registrieren"
 * }.clicks handledBy {
 *     Pages.registration.visit()
 * }
 * ```
 */
@Suppress("unused")
object Pages {

    private val entries = mutableSetOf<Page>()
    val all: Collection<Page> = entries

    private fun <P : Page> register(page: P): P =
        page.also(entries::add)


    private val byParent: Map<Page?, List<Page>> by lazy {
        entries.groupBy(Page::parent)
    }

    /**
     * Gibt alle Pages zurück, die den angegebenen _direkten_ [parent] haben.
     */
    fun getByParent(parent: Page): Collection<Page> =
        byParent[parent] ?: emptySet()

    /**
     * Gibt die Page mit der angegebenen [route] zurück, sofern sie existiert und im Objekt erfasst ist.
     * Ist dies nicht der Fall, wird `null` zurückgegeben.
     *
     * Die zurückgegebene Page ist vom Typ [Page.Internal].
     */
    fun getByRoute(route: String): Page.WithContent? =
        entries.findLast {
            it is Page.WithContent && it.route == route
        }?.let { it as Page.WithContent }


    val root = register(
        Page.Internal(
            parent = null,
            showInMenu = false,
            route = ROOT_PAGE_ROUTE,
            linkTitle = "Willkommen bei Meine Öffentliche!",
            linkTitleShort = "Start",
            content = RenderContext::welcomePage
        )
    )

    val registration = register(Page.Internal(
        parent = root,
        showInMenu = false,
        route = "registration",
        linkTitle = "",
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { registrationStart() }
    ))

    val registrationContinue = register(Page.Internal(
        parent = root,
        showInMenu = false,
        route = "registrationcontinue",
        linkTitle = "",
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { registrationContinue() }
    ))

    val reset = register(Page.Internal(
        parent = root,
        showInMenu = false,
        route = "reset",
        linkTitle = "",
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { resetStart() }
    ))

    val resetContinue = register(Page.Internal(
        parent = root,
        showInMenu = false,
        route = "resetcontinue",
        linkTitle = "",
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { resetContinue() }
    ))

    val retrieveUser = register(Page.Internal(
        parent = root,
        showInMenu = false,
        route = "retrieveUser",
        linkTitle = "",
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { retrieveUserStart() }
    ))

    val retrieveContinue = register(Page.Internal(
        parent = root,
        showInMenu = false,
        route = "retrievecontinue",
        linkTitle = "",
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { retrieveUserContinue() }
    ))

    val contractsOverview = register(Page.Internal(
        parent = root,
        route = "myContractsList",
        linkTitle = "Meine Verträge",
        linkTitleShort = "Verträge",
        icon = CustomIcons.regulations,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.PinAuthenticated,
        /**
         * Wichtig: Die Contracts-Seite ist besonders, da sie die Overview-Page hier niemals angezeigt wird,
         * sondern immer direkt zur Child-Seite gesprungen wird (historisch gewachsen, beide Seiten teilen sie die
         * gleiche Route). Der Content der Vertragsübersicht muss in `contractsList` gepflegt werden.
         */
        content = { /* keiner */ }
    ))

    val contractsList = register(Page.Internal(
        parent = contractsOverview,
        route = "myContractsList",
        linkTitle = "Vertragsübersicht",
        linkTitleShort = "Übersicht",
        content = { page ->
            headerImageLayout {
                title(page.linkTitle)
                headerImage = "images/headers/vertragsuebersicht.jpg"
                content {
                    contractsStart()
                }
            }
        }
    ))


    val myDataOverview = register(Page.Internal(
        parent = root,
        route = "myDataSub",
        linkTitle = "Meine Daten",
        icon = MaterialIconsOutlined.user_attributes,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.Registered,
        content = { page ->
            headerImageLayout {
                title(page.linkTitle)
                headerImage = "images/headers/meinedaten.jpg"
                content {
                    subPagesOverview(page) {
                        description(
                            """An dieser Stelle verwalten Sie Ihre persönlichen Daten: Adresse, Telefonnummern und Ihre
                                | Bankverbindungen.""".trimMargin()
                        )
                    }
                }
            }
        }
    ))

    val myDataContact = register(
        Page.Internal(
            parent = myDataOverview,
            route = "myDataContact",
            linkTitle = "Kontakt${hyphen}informationen",
            linkTitleShort = "Kontakt",
            icon = MaterialIconsOutlined.contact_mail,
            content = RenderContext::contactInformationStart,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.PinAuthenticated
        )
    )

    val myDataBank = register(
        Page.Internal(
            parent = myDataOverview,
            route = "myDataBank",
            linkTitle = "Bank${hyphen}verbindungen",
            linkTitleShort = "IBANs",
            icon = MaterialIconsOutlined.credit_card,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.PinAuthenticated,
            content = RenderContext::bankAccountsPage
        )
    )

    val myDataAccount = register(
        Page.Internal(
            parent = myDataOverview,
            route = "account",
            linkTitle = "Account verwalten",
            linkTitleShort = "Account",
            icon = MaterialIconsOutlined.manage_accounts,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.Registered,
            content = { page ->
                headerImageLayout {
                    title(page.linkTitle)
                    content {
                        subPagesOverview(page)
                    }
                }
            }
        )
    )

    val myDataConsent = register(
        Page.Internal(
            parent = myDataOverview,
            route = "consent",
            linkTitle = "Einwilligungs${hyphen}erklärung",
            linkTitleShort = "Einwilligung",
            icon = CustomIcons.dizupro,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.PinAuthenticated,
            content = RenderContext::consentPage
        )
    )

    val accountChangePassword = register(
        Page.Internal(
            parent = myDataAccount,
            route = "changePassword",
            linkTitle = "Passwort ändern",
            linkTitleShort = "Passwort",
            icon = MaterialIconsOutlined.lock,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.Registered,
            content = RenderContext::updatePasswordPage
        )
    )

    val accountDelete = register(
        Page.Internal(
            parent = myDataAccount,
            route = "deleteAccount",
            linkTitle = "Account löschen",
            linkTitleShort = "Löschen",
            icon = MaterialIconsOutlined.person_cancel,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.Registered,
            content = RenderContext::accountDeletePage
        )
    )


    val inboxOverview = register(Page.Internal(
        parent = root,
        route = "inboxSub",
        linkTitle = "Mein Postfach",
        linkTitleShort = "Postfach",
        icon = MaterialIconsOutlined.local_post_office,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.Registered,
        content = { page ->
            headerImageLayout {
                title(page.linkTitle)
                headerImage = "images/headers/meinpostfach.jpg"
                content {
                    subPagesOverview(page) {
                        description(
                            """Nachrichten zu Ihren Verträgen finden Sie im Posteingang. Sie können auch Ihren
                                | bevorzugten Versandweg anpassen.""".trimMargin()
                        )
                    }
                }
            }
        }
    ))

    val inboxInbox = register(Page.Internal(
        parent = inboxOverview,
        route = "inboxInbox",
        linkTitle = "Posteingang",
        linkTitleShort = "Eingang",
        icon = CustomIcons.posteingang,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.PinAuthenticated,
        content = { inbox() }
    ))

    val inboxDelivery = register(Page.Internal(
        parent = inboxOverview,
        route = "inboxMailType",
        linkTitle = "Versandweg ändern",
        linkTitleShort = "Versandweg",
        icon = MaterialIconsOutlined.send,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.PinAuthenticated,
        content = { delivery() }
    ))


    val claimOverview = register(Page.Internal(
        parent = root,
        route = "claimSub",
        linkTitle = "Schaden",
        icon = CustomIcons.vase_crack,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.Registered,
        content = { page ->
            headerImageLayout {
                title(page.linkTitle)
                headerImage = "images/headers/schaeden.jpg"
                content {
                    subPagesOverview(page)
                }
            }
        }
    ))

    val claimList = register(Page.Internal(
        parent = claimOverview,
        route = "claimList",
        linkTitle = "Schadenübersicht",
        linkTitleShort = "Übersicht",
        icon = MaterialIconsOutlined.car_crash,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.PinAuthenticated,
        content = { subPageClaimList() }
    ))

    val claimReport = register(
        Page.External(
            parent = claimOverview,
            url = "https://sfa.oeffentliche.de/static/schaden-online2/index.html#page=main",
            linkTitle = "Schaden melden",
            icon = MaterialIconsOutlined.campaign
        )
    )

    val claimRebuy = register(
        Page.External(
            parent = claimOverview,
            url = "https://www.oeffentliche.de/content/service/schaden-services/schadenrueckkaufrechner/",
            linkTitle = "Schaden${hyphen}rückkaufrechner",
            icon = MaterialIconsOutlined.calculate
        )
    )


    val serviceOverview = register(Page.Internal(
        parent = root,
        route = "serviceSub",
        linkTitle = "Self-Services",
        linkTitleShort = "Services",
        icon = MaterialIconsOutlined.build,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.Registered,
        content = { page ->
            headerImageLayout {
                title(page.linkTitle)
                headerImage = "images/headers/selfservices.jpg"
                content {
                    subPagesOverview(page)
                }
            }
        }
    ))

    val serviceCarInsurance = register(
        Page.Internal(
            parent = serviceOverview,
            route = "serviceCarInsurance",
            linkTitle = "Kfz-Versicherung",
            linkTitleShort = "Kfz",
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.Registered,
            icon = CustomIcons.kfz,
            content = { page ->
                headerImageLayout {
                    title(page.linkTitle)
                    content {
                        subPagesOverview(page)
                    }
                }
            }
        )
    )

    val serviceCarInsuranceDeregister = register(
        Page.Internal(
            parent = serviceCarInsurance,
            route = "serviceCarInsuranceDeregister",
            linkTitle = "Fahrzeug Abmeldung/Verkauf",
            linkTitleShort = "Abmelden",
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.PinAuthenticated,
            icon = MaterialIconsOutlined.directions_car,
            content = RenderContext::deregisterCarStart
        )
    )

    val serviceCarInsuranceCard = register(Page.Internal(
        parent = serviceCarInsurance,
        route = "serviceCarInsuranceCard",
        linkTitle = "Internationale Versicherungskarte",
        linkTitleShort = "I.V.-Karte",
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.PinAuthenticated,
        icon = MaterialIconsOutlined._public,
        content = { intInsCardStart() }
    ))

    val serviceCarInsuranceDigiCard = register(Page.Internal(
        parent = serviceCarInsurance,
        route = "serviceCarDigiCard",
        linkTitle = "Digitale Kfz-Servicekarte",
        linkTitleShort = "Digi-Card",
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.PinAuthenticated,
        icon = MaterialIconsOutlined.install_mobile,
        content = { digitalServiceCardStart() }
    ))

    val serviceCarTaxCertificates = register(
        Page.Internal(
            parent = serviceCarInsurance,
            route = "serviceCarTaxCertificates",
            linkTitle = "Steuer${hyphen}bescheinigungen",
            linkTitleShort = "Steuer",
            icon = CustomIcons.regulations,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.PinAuthenticated,
            content = { taxStart() }
        )
    )

    val serviceLife = register(
        Page.Internal(
            parent = serviceOverview,
            route = "serviceLife",
            linkTitle = "Lebens${hyphen}versicherung",
            linkTitleShort = "Leben",
            icon = MaterialIconsOutlined.volunteer_activism,
            roleForMenu = PortalAuthority.Registered,
            roleForRoute = PortalAuthority.Registered,
            content = { page ->
                headerImageLayout {
                    title(page.linkTitle)
                    content {
                        subPagesOverview(page)
                    }
                }
            }
        )
    )

    val serviceLifeEntryNotification = register(Page.Internal(
        parent = serviceLife,
        route = "serviceLifeEntryNotification",
        linkTitle = "Wertstands${hyphen}mitteilung",
        linkTitleShort = "Wertstand",
        icon = MaterialIconsOutlined.savings,
        roleForMenu = PortalAuthority.Registered,
        roleForRoute = PortalAuthority.PinAuthenticated,
        content = { lifeValueStart() }
    ))

    val extrasOverview = register(Page.Internal(
        parent = root,
        route = "extras",
        linkTitle = "Extras für Sie",
        icon = MaterialIconsOutlined.featured_seasonal_and_gifts,
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { page ->
            headerImageLayout {
                title(page.linkTitle)
                headerImage = "images/headers/extras.jpg"
                content {
                    subPagesOverview(page) {
                        description(
                            """Das Leben ist bunt, voller Bewegung und manchmal etwas kompliziert - wir wollen es Ihnen 
                                | mit unseren Extras ein wenig einfacher machen.""".trimMargin()
                        )
                    }
                }
            }
        }
    ))

    val extrasWelcome2BS = register(
        Page.External(
            parent = extrasOverview,
            url = "https://www.oeffentliche.de/content/service/wohnen/welcome2BS/",
            linkTitle = "Welcome2BS!",
            icon = MaterialIconsOutlined.waving_hand
        )
    )

    val extrasMovingBoxes = register(
        Page.External(
            parent = extrasOverview,
            url = "https://www.oeffentliche.de/content/service/wohnen/umzugskartons/",
            linkTitle = "Umzugskartons abholen",
            icon = MaterialIconsOutlined.package_2
        )
    )

    val extrasTrailerRental = register(
        Page.External(
            parent = extrasOverview,
            url = "https://www.oeffentliche.de/content/service/wohnen/umzugsanhaenger/",
            linkTitle = "Anhänger ausleihen",
            icon = CustomIcons.anhaenger
        )
    )

    val contactOverview = register(Page.Internal(
        parent = root,
        route = "contact",
        linkTitle = "Kontakt",
        icon = MaterialIconsOutlined.chat_bubble,
        roleForRoute = PortalAuthority.None,
        roleForMenu = PortalAuthority.None,
        content = { page ->
            headerImageLayout {
                title(page.linkTitle)
                headerImage = "images/headers/kontakt.jpg"
                headerImageIsDark = true
                content {
                    subPagesOverview(page) {
                        description(
                            """Schreiben Sie uns! Über diese Kacheln können Sie Ihre persönliche Beraterin oder Ihren
                                | persönlichen Berater kontaktieren, Termine vereinbaren oder uns Anregungen zu Meine
                                | Öffentliche senden. Wir bleiben in Kontakt.""".trimMargin()
                        )
                    }
                }
            }
        }
    ))

    val contactCallback = register(
        Page.External(
            parent = contactOverview,
            url = "https://outlook.office365.com/owa/calendar/ffentlicheVersicherungBraunschweig4@oeffentliche.de/bookings/",
            linkTitle = "Rückrufservice",
            icon = MaterialIconsOutlined.call
        )
    )

    val contactMessage = register(
        Page.InternalWithFallbackUrl(
            decorated = Page.Internal(
                parent = contactOverview,
                roleForMenu = PortalAuthority.None,
                roleForRoute = PortalAuthority.PinAuthenticated,
                route = "contactMessage",
                linkTitle = "Mitteilung schreiben",
                linkTitleShort = "Mitteilung",
                icon = MaterialIconsOutlined.rate_review,
                content = RenderContext::contactMessagePage
            ),
            hasAuthority = PortalSession::hasAuthority,
            fallbackUrl = "https://www.oeffentliche.de/content/service/kontakt/ihre-mitteilung-an-uns/"
        )
    )

    val contactErrorForm = register(
        Page.External(
            parent = contactOverview,
            url = "https://www.oeffentliche.de/content/service/kundenportal/hilfe/",
            linkTitle = "Fehler melden",
            icon = MaterialIconsOutlined.chat_error
        )
    )
}

/**
 * Gibt eine Collection der _unmittelbaren_ Children der jeweiligen Seite zurück.
 */
val Page.children: Collection<Page>
    get() = Pages.getByParent(this)

/**
 * Gibt einen Flow zurück, der die Seite zur aktuellen Route emittiert, sofern diese existiert
 * oder `null`, wenn für die aktuelle Route keine Page hinterlegt ist.
 *
 * Da nur [Page.WithContent]s eine Route haben und über diese angesteuert werden können, ist der Rückgabewert hier
 * `Flow<Page.WithContent?>`
 */
val KupoRouter.activePage: Flow<Page.WithContent?>
    get() = route.map { route ->
        route?.let(Pages::getByRoute)
    }